import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import {
  Modal,
  Text,
  Button,
  Group,
  Stack,
  Paper,
  Title,
  Alert,
  ActionIcon,
  Avatar,
  Switch,
  Badge,
  Tooltip,
  Loader,
} from "@mantine/core";
import { IconMinus, IconPlus, IconUsersGroup } from "@tabler/icons-react";

function EditOrgLevelMembersModal({
  isOpen,
  onClose,
  orgLevel,
  currentMembers = [],
  availableUsers = [],
  onMembersUpdated,
  role = "member",
  roleLabel = "Members",
  roleLabelSingular = "Member",
}) {
  const { getVerifiedJWT } = useAuth();
  const [loadingStates] = useState({});
  const [error, setError] = useState(null);
  const [localMembers, setLocalMembers] = useState(currentMembers);
  const [localAvailableUsers, setLocalAvailableUsers] =
    useState(availableUsers);
  const [hasChanges, setHasChanges] = useState(false);
  const [isProcessingDownline, setIsProcessingDownline] = useState(false);
  const [confirmRemoveUser, setConfirmRemoveUser] = useState(null);

  const sortUsersByName = users => {
    return [...users].sort((a, b) => {
      const nameA = `${a.first_name || ""} ${a.last_name || ""}`
        .trim()
        .toLowerCase();
      const nameB = `${b.first_name || ""} ${b.last_name || ""}`
        .trim()
        .toLowerCase();
      const displayNameA =
        nameA || (a.name || a.email || "Unknown User").toLowerCase();
      const displayNameB =
        nameB || (b.name || b.email || "Unknown User").toLowerCase();
      return displayNameA.localeCompare(displayNameB);
    });
  };

  useEffect(() => {
    setLocalMembers(sortUsersByName(currentMembers));
    setLocalAvailableUsers(sortUsersByName(availableUsers));
    setHasChanges(false);
  }, [currentMembers, availableUsers]);

  const updateMember = async (user, include, removeDownline = false) => {
    if (!orgLevel?.id || !user?.user_id) return;

    setError(null);

    if (include) {
      const userToAdd = localAvailableUsers.find(
        u => u.user_id === user.user_id
      );
      if (userToAdd) {
        setLocalMembers(prev => sortUsersByName([...prev, userToAdd]));
        setLocalAvailableUsers(prev =>
          sortUsersByName(prev.filter(u => u.user_id !== user.user_id))
        );
      }
    } else {
      const userToRemove = localMembers.find(u => u.user_id === user.user_id);
      if (userToRemove) {
        setLocalAvailableUsers(prev =>
          sortUsersByName([...prev, userToRemove])
        );
        setLocalMembers(prev =>
          sortUsersByName(prev.filter(u => u.user_id !== user.user_id))
        );
      }
    }

    setHasChanges(true);

    (async () => {
      try {
        const jwt = await getVerifiedJWT();
        const isManagerRole = role === "manager" || role === "admin";
        const endpoint = isManagerRole
          ? "/v1/org-level/entity/manager"
          : "/v1/org-level/entity/user";
        const body = isManagerRole
          ? {
              org_level_entity_id: orgLevel.id,
              user_id: user.user_id,
              is_primary_manager: role === "manager",
              include: include,
            }
          : {
              org_level_entity_id: orgLevel.id,
              user_id: user.user_id,
              include: include,
            };
        if (!include && !isManagerRole) {
          body.remove_downline = removeDownline;
        }
        const response = await fetch(getApiEndpoint(endpoint), {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          throw new Error("Failed to update sales group members");
        }

        if (!include && removeDownline && onMembersUpdated) {
          onMembersUpdated();
        }
      } catch (err) {
        setError(err.message);
      }
    })();
  };

  const handleAddUser = user => {
    updateMember(user, true);
  };

  const handleRemoveUser = user => {
    if (user.include_downline) {
      setConfirmRemoveUser(user);
    } else {
      updateMember(user, false, false);
    }
  };

  const confirmRemove = removeDownline => {
    if (confirmRemoveUser) {
      updateMember(confirmRemoveUser, false, removeDownline);
    }
    setConfirmRemoveUser(null);
  };

  const handleToggleIncludeDownline = async user => {
    if (!user?.user_id || !orgLevel?.id) {
      setError(
        "Unable to toggle: required data not available. Try refreshing the page."
      );
      return;
    }

    const newIncludeDownlineValue = !user.include_downline;

    setLocalMembers(prev =>
      prev.map(u =>
        u.user_id === user.user_id
          ? { ...u, include_downline: newIncludeDownlineValue }
          : u
      )
    );

    setHasChanges(true);
    setError(null);

    if (newIncludeDownlineValue) {
      setIsProcessingDownline(true);
    }

    (async () => {
      try {
        const jwt = await getVerifiedJWT();
        const response = await fetch(
          getApiEndpoint("/v1/org-level/entity/user"),
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify({
              org_level_entity_id: orgLevel.id,
              user_id: user.user_id,
              include: true,
              include_downline: newIncludeDownlineValue,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to update include downline setting");
        }

        if (newIncludeDownlineValue && onMembersUpdated) {
          onMembersUpdated();
        }
      } catch (err) {
        setLocalMembers(prev =>
          prev.map(u =>
            u.user_id === user.user_id
              ? { ...u, include_downline: !newIncludeDownlineValue }
              : u
          )
        );
        setError(err.message);
      } finally {
        setIsProcessingDownline(false);
      }
    })();
  };

  const handleClose = () => {
    setError(null);
    if (hasChanges && onMembersUpdated) {
      onMembersUpdated();
    }
    onClose();
  };

  const getUserDisplayName = user => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.name || user.email || "Unknown User";
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={`Edit ${roleLabel} - ${orgLevel?.name || "Sales Group"}`}
      size="lg"
      centered
    >
      <Stack gap="lg">
        {error && (
          <Alert variant="light" color="red" title="Error">
            {error}
          </Alert>
        )}

        <Paper p="md" withBorder>
          <Group justify="space-between" align="center" mb="md">
            <Group gap="xs">
              <Title order={4}>
                {roleLabel} ({localMembers.length})
              </Title>
              {isProcessingDownline && <Loader size="xs" color="grape" />}
            </Group>
            {role === "member" && (
              <Text size="xs" c="dimmed" fw={500}>
                Include Downline
              </Text>
            )}
          </Group>
          {localMembers.length > 0 ? (
            <Stack gap="sm">
              {localMembers.map((user, index) => (
                <Group
                  key={user.user_id || index}
                  gap="xs"
                  p="xs"
                  style={{
                    borderRadius: "6px",
                    backgroundColor: "var(--mantine-color-gray-0)",
                  }}
                >
                  <Avatar src={user.profile_image} size="sm" radius="xl" />
                  <Stack gap={1} style={{ flex: 1 }}>
                    <Group gap="xs">
                      <Text fw={500} size="sm">
                        {getUserDisplayName(user)}
                      </Text>
                      {role === "member" && user.include_downline && (
                        <Badge
                          size="xs"
                          color="grape"
                          variant="light"
                          leftSection={<IconUsersGroup size={10} />}
                        >
                          Includes Downline
                        </Badge>
                      )}
                    </Group>
                    {user.email && (
                      <Text size="xs" c="dimmed">
                        {user.email}
                      </Text>
                    )}
                    {user.role && (
                      <Text size="xs" c="dimmed">
                        {user.role}
                      </Text>
                    )}
                  </Stack>
                  {role === "member" && (
                    <Tooltip
                      label={
                        user.include_downline
                          ? "Disable auto-add downline"
                          : "Enable auto-add downline"
                      }
                      position="left"
                    >
                      <Switch
                        size="sm"
                        checked={user.include_downline || false}
                        onChange={() => handleToggleIncludeDownline(user)}
                        disabled={loadingStates[`toggle-${user.user_id}`]}
                        color="grape"
                      />
                    </Tooltip>
                  )}
                  <ActionIcon
                    size="sm"
                    variant="light"
                    color="red"
                    onClick={() => handleRemoveUser(user)}
                    loading={loadingStates[`${user.user_id}-false`]}
                    disabled={loadingStates[`${user.user_id}-false`]}
                  >
                    <IconMinus size={14} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          ) : (
            <Text c="dimmed" style={{ fontStyle: "italic" }}>
              No {roleLabel.toLowerCase()} in this sales group
            </Text>
          )}
        </Paper>

        <Paper p="md" withBorder>
          <Title order={4} mb="md">
            Available Users ({localAvailableUsers.length})
          </Title>
          {localAvailableUsers.length > 0 ? (
            <Stack gap="sm">
              {localAvailableUsers.map((user, index) => (
                <Group
                  key={user.user_id || index}
                  gap="xs"
                  p="xs"
                  style={{
                    borderRadius: "6px",
                    backgroundColor: "var(--mantine-color-gray-0)",
                  }}
                >
                  <Avatar src={user.profile_image} size="sm" radius="xl" />
                  <Stack gap={1} style={{ flex: 1 }}>
                    <Text fw={500} size="sm">
                      {getUserDisplayName(user)}
                    </Text>
                    {user.email && (
                      <Text size="xs" c="dimmed">
                        {user.email}
                      </Text>
                    )}
                    {user.role && (
                      <Text size="xs" c="dimmed">
                        {user.role}
                      </Text>
                    )}
                  </Stack>
                  <ActionIcon
                    size="sm"
                    variant="light"
                    color="green"
                    onClick={() => handleAddUser(user)}
                    loading={loadingStates[`${user.user_id}-true`]}
                    disabled={loadingStates[`${user.user_id}-true`]}
                  >
                    <IconPlus size={14} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          ) : (
            <Text c="dimmed" style={{ fontStyle: "italic" }}>
              No additional users available
            </Text>
          )}
        </Paper>

        <Group justify="flex-end" gap="sm">
          <Button variant="light" onClick={handleClose}>
            Close
          </Button>
        </Group>
      </Stack>

      <Modal
        opened={!!confirmRemoveUser}
        onClose={() => setConfirmRemoveUser(null)}
        title="Remove downline?"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            <strong>
              {confirmRemoveUser ? getUserDisplayName(confirmRemoveUser) : ""}
            </strong>{" "}
            has their downline included in this group. Do you also want to
            remove their downline?
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="outline" onClick={() => confirmRemove(false)}>
              No, keep downline
            </Button>
            <Button color="red" onClick={() => confirmRemove(true)}>
              Yes, remove downline
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Modal>
  );
}

export default EditOrgLevelMembersModal;
